/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DynamicStepRef } from "@companion-core/shared/app/Components/DynamicForm/DynamicStep";
import {
  useWizardStore,
  useWizardStoreWithProvider,
} from "@companion-core/shared/app/Store/wizardStore";
import { trackClickDynamicForm } from "@companion-core/shared/app/tracking/dynamicForm";
import {
  getFirstStep,
  getNextStep,
  hasFormPath,
  isStepValid,
} from "@companion-core/shared/app/Utils/dynamicForm";
import { getUpdatedFields, toApiPayload } from "@companion-core/shared/app/Utils/wizardHelper";
import { useTrackingStore } from "@companion-core/shared/app/Store/trackingStore";
import { DynamicFormProps } from "@companion-core/shared/app/Components/DynamicForm/dynamicForm.model";

type UseDynamicFormParams = Pick<
  DynamicFormProps,
  "initialFormJSON" | "useProvider" | "onSubmitForm" | "onSubmitConclude"
>;

export const useDynamicForm = ({
  initialFormJSON,
  useProvider = false,
  onSubmitForm,
  onSubmitConclude,
}: UseDynamicFormParams) => {
  const {
    fields,
    haveSummary,
    summaryTitle,
    currentStep,
    currentForm,
    defaultApiValues,
    formTree,
    submitButtonLoading,
    resetWizard,
    addStepToFormTree,
    addFirstStepToFormTree,
    deleteStepsAfterIndex,
    setFields,
    setCurrentStep,
    setCurrentForm,
    setDefaultApiValues,
    setHaveSummary,
    setSummaryTitle,
    setSubmitButtonLoading,
  } = useProvider ? useWizardStoreWithProvider() : useWizardStore();

  const { t } = useTranslation();
  const { setCurrentPage } = useTrackingStore();
  const [summaryStepAdded, setSummaryStepAdded] = useState<boolean>(false);
  const dynamicStepRef = useRef<DynamicStepRef>(null);
  const numberOfSteps = useMemo(() => currentForm?.steps.length, [currentForm, summaryStepAdded]);
  const isOneStepForm = useMemo(() => numberOfSteps === 1, [numberOfSteps]);
  const initializeForm = useCallback(() => {
    setHaveSummary(initialFormJSON.summary ?? false);
    setSummaryTitle(initialFormJSON.summaryTitle ?? "");
    setCurrentForm(initialFormJSON);
    setDefaultApiValues(initialFormJSON.defaultApiValues ?? {});
    const step = getFirstStep(initialFormJSON);
    setCurrentStep(step);
    if (step) {
      addFirstStepToFormTree(step?.order, initialFormJSON.jsonPath, initialFormJSON);
    }
    setCurrentPage(step?.tracking?.name);
  }, [initialFormJSON]);

  useEffect(() => {
    if (currentForm && currentStep) return; // We don't want to reset the form if we already have a form
    initializeForm();
  }, [initializeForm]);

  const addSummaryStep = () => {
    if (!numberOfSteps) return;
    const summaryStep = {
      order: numberOfSteps + 1,
      fields: [],
      type: "summary",
      title: summaryTitle,
    };
    currentForm?.steps.push(summaryStep);
    setSummaryStepAdded(true);
  };

  // Final step is the last step of the complete form
  const isFinalStep = useMemo(() => {
    if (!currentStep) return false;

    let isFinalStep = false;
    if (isOneStepForm || currentStep?.order === numberOfSteps) {
      const hasFormPathInField = currentStep?.fields.some((field) => hasFormPath(field));
      isFinalStep = !hasFormPathInField;
    }
    if (isFinalStep && haveSummary && !summaryStepAdded) {
      addSummaryStep();
      return false;
    }
    return isFinalStep || currentStep?.type === "summary";
  }, [isOneStepForm, currentStep, numberOfSteps, currentForm]);

  const incrementStep = ({
    onInvalidStep,
    onValidStep,
  }: { onInvalidStep?: () => void; onValidStep?: () => void } = {}) => {
    if (!currentStep || !currentForm || !dynamicStepRef.current) return;
    dynamicStepRef.current.validateAllFields();
    if (!isStepValid(currentStep)) {
      onInvalidStep?.();
      return;
    }

    const updatedFields = getUpdatedFields(fields, currentStep, currentForm.jsonPath);
    setFields(updatedFields);
    const { step, jsonForm } = getNextStep(
      currentForm,
      currentStep,
      formTree,
      addStepToFormTree,
      deleteStepsAfterIndex
    );
    if (step?.tracking) {
      const { name = "", chapter1 = "", chapter2 = "", chapter3 = "", site_level2 } = step.tracking;
      trackClickDynamicForm({
        clickName: name,
        click_chapter1: chapter1,
        click_chapter2: chapter2,
        click_chapter3: chapter3,
        site_level2,
      });
    } else if (step?.type === "summary" && currentStep.tracking) {
      const { chapter1 = "", chapter2 = "", site_level2 } = currentStep.tracking;
      trackClickDynamicForm({
        clickName: "step_summary",
        click_chapter1: chapter1,
        click_chapter2: chapter2,
        site_level2,
      });
    }
    setCurrentPage(step?.tracking?.name);
    setDefaultApiValues({ ...defaultApiValues, ...jsonForm.defaultApiValues });
    setCurrentForm(jsonForm);
    if (step) {
      setCurrentStep(step);
      onValidStep?.();
    }
  };

  const submitForm = async () => {
    if (!currentStep || !currentForm) return;
    if (dynamicStepRef.current) dynamicStepRef.current.validateAllFields();
    if (!isStepValid(currentStep)) return;
    setSubmitButtonLoading(true);
    const updatedFields = getUpdatedFields(fields, currentStep, currentForm.jsonPath);
    setFields(updatedFields);
    const payload = toApiPayload(updatedFields, defaultApiValues);
    try {
      await onSubmitForm({ ...payload });
      resetWizard();
      if (onSubmitConclude) onSubmitConclude();
    } finally {
      setSubmitButtonLoading(false);
    }
  };

  return {
    t,
    submitButtonLoading,
    currentForm,
    currentStep,
    isFinalStep,
    dynamicStepRef,
    incrementStep,
    submitForm,
  };
};
